import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import CosmicField from "@/components/CosmicField";
import PaywallModal from "@/components/PaywallModal";
import { Compass, ArrowRight, Gift, Star } from "lucide-react";

/**
 * Root landing page (`/`) — intentionally stripped-down.
 * Purpose: sign up for an account (or sign in) and pay in one flow.
 * Longer story / mechanics / branches live at `/about`.
 *
 * Modes:
 *   welcome  → hero + "Sign up for an account" / "Sign in"
 *   register → email/password/name form
 *   login    → email/password form
 *   pay      → Stripe paywall shown inline (after successful registration)
 */
export default function Landing() {
    const { user, login, register } = useAuth();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const refFromUrl = (sp.get("ref") || "").trim().toUpperCase() || null;

    const [mode, setMode] = useState(refFromUrl ? "register" : "welcome");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [asOrg, setAsOrg] = useState(false);
    const [orgName, setOrgName] = useState("");
    const [orgDomain, setOrgDomain] = useState("");
    const [orgWebsite, setOrgWebsite] = useState("");
    const [referrer, setReferrer] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // Once someone has just registered we hold them here and show the
    // paywall inline instead of bouncing to /onboarding — the pay portal
    // is part of the signup flow.
    const [justRegistered, setJustRegistered] = useState(false);

    // Look up the referrer's public name for the invite banner.
    React.useEffect(() => {
        if (!refFromUrl) return;
        api.get(`/referrals/lookup/${refFromUrl}`)
            .then((r) => setReferrer(r.data))
            .catch(() => setReferrer({ name: null, username: null, invalid: true }));
    }, [refFromUrl]);

    // If already logged in on load, route them — but NOT if they just
    // registered on this session (we want them to see the pay step first).
    React.useEffect(() => {
        if (justRegistered) return;
        if (user && user.has_access) nav("/atlas");
        else if (user && !user.onboarded) nav("/onboarding");
    }, [user, nav, justRegistered]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        const r =
            mode === "login"
                ? await login(email, password)
                : await register(
                      email,
                      password,
                      name,
                      asOrg
                          ? {
                                as_organization: true,
                                org_name: orgName,
                                org_domain: orgDomain,
                                org_website: orgWebsite,
                            }
                          : null,
                      refFromUrl
                  );
        setBusy(false);
        if (!r.ok) {
            setError(r.error);
            return;
        }
        if (mode === "register") {
            // Straight to Stripe: new signups jump directly to the Stripe
            // checkout so email → payment is one seamless flow. If the
            // checkout call fails for any reason (network, Stripe outage),
            // gracefully fall back to the on-page paywall so the user can
            // still complete payment manually.
            setJustRegistered(true);
            try {
                const { data } = await api.post("/payments/checkout", {
                    origin_url: window.location.origin,
                });
                if (data?.checkout_url) {
                    window.location.href = data.checkout_url;
                    return;
                }
            } catch {
                /* fall through to on-page paywall */
            }
            setMode("pay");
        } else {
            // Sign in — respect existing routing rules.
            if (r.user.has_access) nav("/atlas");
            else if (!r.user.onboarded) nav("/onboarding");
            else nav("/paywall");
        }
    };

    return (
        <div className="relative min-h-screen w-full cosmic-sky overflow-hidden">
            <CosmicField count={80} />

            {/* Node cluster — kept as the visual signature */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-[520px] h-[520px]">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <motion.div
                            key={i}
                            className="absolute w-3 h-3 rounded-full"
                            style={{
                                left: `${50 + 34 * Math.cos((i / 6) * Math.PI * 2)}%`,
                                top: `${50 + 34 * Math.sin((i / 6) * Math.PI * 2)}%`,
                                background: ["#FF2052", "#FFC107", "#3EB489", "#6F00FF", "#FFFFFF", "#8BCDF8"][i],
                                boxShadow: `0 0 24px ${["#FF2052", "#FFC107", "#3EB489", "#6F00FF", "#FFFFFF", "#8BCDF8"][i]}`,
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: [1, 1.15, 1] }}
                            transition={{ delay: 0.2 + i * 0.08, duration: 3, repeat: Infinity, repeatType: "reverse" }}
                        />
                    ))}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <line
                                key={i}
                                x1="50"
                                y1="50"
                                x2={50 + 34 * Math.cos((i / 6) * Math.PI * 2)}
                                y2={50 + 34 * Math.sin((i / 6) * Math.PI * 2)}
                                stroke="rgba(255,255,255,0.35)"
                                strokeWidth="0.4"
                                strokeDasharray="0.6 0.8"
                            />
                        ))}
                    </svg>
                    <motion.div
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        animate={{ scale: [1, 1.08, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                            <Compass className="w-8 h-8 text-humanities" />
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16">
                <AnimatePresence mode="wait">
                    {mode === "welcome" && (
                        <motion.div
                            key="welcome"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="text-center max-w-4xl"
                        >
                            <p
                                className="chip mx-auto mb-8 text-base sm:text-lg px-4 py-1.5"
                                style={{ background: "rgba(255,255,255,0.35)", color: "#0A2540" }}
                            >
                                <span className="w-2 h-2 rounded-full bg-arts" /> Project Atlas
                            </p>
                            <h1
                                className="font-display font-black text-6xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tighter text-white leading-[0.9]"
                                style={{ textShadow: "0 4px 40px rgba(10,37,64,0.4)" }}
                                data-testid="hero-title"
                            >
                                Welcome to Atlas.
                            </h1>

                            <p
                                className="text-white/95 text-xl sm:text-2xl md:text-3xl mt-8 max-w-3xl mx-auto leading-relaxed"
                                style={{ textShadow: "0 2px 20px rgba(10,37,64,0.35)" }}
                                data-testid="hero-subtitle"
                            >
                                Project Atlas is the missing link between what you study and what
                                you actually do — fill your resume with legit work, meet your
                                professional network, and connect collaborations that belong
                                together.
                            </p>
                            <p
                                className="text-white font-display font-black text-2xl sm:text-3xl md:text-4xl mt-6 tracking-tight"
                                style={{ textShadow: "0 2px 20px rgba(10,37,64,0.4)" }}
                                data-testid="hero-tagline"
                            >
                                <span className="text-arts">Real experience.</span>{" "}
                                <span className="text-trades">Real connection.</span>{" "}
                                <span className="text-humanities">Real projects.</span>
                            </p>

                            <div className="mt-12 flex flex-wrap gap-4 justify-center">
                                <button
                                    onClick={() => setMode("register")}
                                    className="arcade-btn"
                                    data-variant="arts"
                                    data-testid="cta-signup"
                                >
                                    Sign up for an account <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setMode("login")}
                                    className="arcade-btn"
                                    data-testid="cta-signin"
                                >
                                    I already have an account — Sign in
                                </button>
                            </div>

                            <HomeBullets onSignup={() => setMode("register")} />
                        </motion.div>
                    )}

                    {(mode === "login" || mode === "register") && (
                        <motion.div
                            key={mode}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="crystal rounded-3xl p-8 w-full max-w-md"
                        >
                            {mode === "register" && referrer && referrer.name && (
                                <div
                                    className="mb-4 rounded-2xl px-3 py-2.5 flex items-start gap-2 bg-emerald-50 text-emerald-800"
                                    data-testid="referrer-banner"
                                >
                                    <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm leading-tight">
                                        <span className="font-display font-bold">{referrer.name}</span>{" "}
                                        invited you. Your first month is on us — sign up below.
                                    </div>
                                </div>
                            )}
                            {mode === "register" && refFromUrl && referrer && referrer.invalid && (
                                <div
                                    className="mb-4 rounded-2xl px-3 py-2.5 text-sm bg-amber-50 text-amber-800"
                                    data-testid="referrer-invalid"
                                >
                                    That invite code doesn't match anyone — you can still sign up as normal.
                                </div>
                            )}
                            <h2 className="font-display font-black text-3xl tracking-tighter text-slate-900">
                                {mode === "login" ? "Welcome back, Explorer" : "Sign up for an account"}
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">
                                {mode === "login"
                                    ? "Sign in to return to your workshops."
                                    : "Every contribution is stamped on a permanent, verifiable resume."}
                            </p>

                            <form onSubmit={submit} className="mt-6 space-y-3">
                                {mode === "register" && (
                                    <input
                                        type="text"
                                        placeholder="Your name"
                                        className="field-input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        data-testid="register-name"
                                    />
                                )}
                                {mode === "register" && (
                                    <label
                                        className="flex items-start gap-2 px-3 py-2 rounded-xl bg-white/60 border border-slate-200 cursor-pointer select-none"
                                        data-testid="register-as-org-wrap"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1"
                                            checked={asOrg}
                                            onChange={(e) => setAsOrg(e.target.checked)}
                                            data-testid="register-as-org"
                                        />
                                        <span className="text-sm leading-tight">
                                            <span className="font-display font-bold text-slate-900 block">
                                                I'm signing up as an Organization
                                            </span>
                                            <span className="text-slate-500 text-xs">
                                                Post recruitment listings. Auto-verified when your
                                                email domain matches your org.
                                            </span>
                                        </span>
                                    </label>
                                )}
                                {mode === "register" && asOrg && (
                                    <div className="space-y-2 pl-1">
                                        <input
                                            type="text"
                                            placeholder="Organization name (e.g., Acme Inc)"
                                            className="field-input"
                                            value={orgName}
                                            onChange={(e) => setOrgName(e.target.value)}
                                            required
                                            data-testid="register-org-name"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Organization email domain (e.g., acme.com)"
                                            className="field-input"
                                            value={orgDomain}
                                            onChange={(e) => setOrgDomain(e.target.value)}
                                            required
                                            data-testid="register-org-domain"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Website (optional) — e.g. acme.com or https://acme.com"
                                            className="field-input"
                                            value={orgWebsite}
                                            onChange={(e) => setOrgWebsite(e.target.value)}
                                            data-testid="register-org-website"
                                        />
                                        <p className="text-[11px] text-slate-500">
                                            Tip: use an email at your org domain to earn the
                                            Verified badge automatically.
                                        </p>
                                    </div>
                                )}
                                <input
                                    type="email"
                                    placeholder="you@example.com"
                                    className="field-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={(e) => {
                                        // Fire-and-forget abandoned-signup lead capture.
                                        // Only when the user is on the register form and
                                        // typed a real-looking email — we never track
                                        // sign-in emails.
                                        if (mode !== "register") return;
                                        const v = (e.target.value || "").trim().toLowerCase();
                                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
                                        api.post("/leads/abandoned", {
                                            email: v,
                                            source: "landing_signup_abandoned",
                                            referral_code: refFromUrl || null,
                                        }).catch(() => {});
                                    }}
                                    required
                                    data-testid={mode === "login" ? "login-email" : "register-email"}
                                />
                                <input
                                    type="password"
                                    placeholder="Password (min 6 chars)"
                                    className="field-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid={mode === "login" ? "login-password" : "register-password"}
                                />
                                {error && (
                                    <div
                                        className="text-sm text-white bg-sciences px-3 py-2 rounded-xl"
                                        data-testid="auth-error"
                                    >
                                        {error}
                                    </div>
                                )}
                                <button
                                    disabled={busy}
                                    className="arcade-btn w-full"
                                    data-variant="primary"
                                    data-testid={mode === "login" ? "login-submit" : "register-submit"}
                                >
                                    {busy
                                        ? "Working…"
                                        : mode === "login"
                                        ? "Sign in"
                                        : "Create account & continue to payment"}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>

                            <div className="flex justify-between items-center mt-5 text-sm">
                                <button
                                    onClick={() => setMode("welcome")}
                                    className="text-slate-500 hover:text-slate-900"
                                    data-testid="back-welcome"
                                >
                                    ← Back
                                </button>
                                <button
                                    onClick={() => {
                                        setError(null);
                                        setMode(mode === "login" ? "register" : "login");
                                    }}
                                    className="text-humanities font-bold"
                                    data-testid="toggle-mode"
                                >
                                    {mode === "login" ? "Need an account?" : "Have an account?"}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Inline pay portal — shown as step 2 of signup, right on this page. */}
            <PaywallModal
                open={mode === "pay"}
                onClose={null}
                reason="Your account is ready. Unlock full Atlas Access to start contributing."
            />
        </div>
    );
}

/**
 * Home-page persuasion grid — nine hand-picked reasons Atlas beats LinkedIn,
 * plus a veteran offer callout. Rendered directly on the welcome screen so
 * visitors see the value proposition before they ever click "Sign up".
 */
function HomeBullets({ onSignup }) {
    const { user } = useAuth();
    const onVetCta = () => {
        if (user?.id) {
            window.location.href = "/settings/verify-vet";
        } else {
            onSignup();
        }
    };
    const items = [
        {
            title: "Get discovered without cold-applying.",
            body: "Employers, projects, mentors, and internships browse for the contributions that surface here.",
        },
        {
            title: "Bring people together around a common goal.",
            body: "Rally collaborators, mentors, and teammates — and never lose track of who did what.",
        },
        {
            title: "Turn educational projects into public wins.",
            body: "Thesis, class hackathon, group work — stamped, dated, permanent.",
        },
        {
            title: "Get matched with collaborators outside your bubble.",
            body: "Atlas surfaces people whose disciplines complement yours.",
        },
        {
            title: "Cross the visa barrier with proof, not translation.",
            body: "Recruiters see what you shipped in English — no need to parse a foreign transcript.",
        },
        {
            title: "Roadmaps for any curiosity.",
            body: "Marine biology, jazz theory, property law — receive a path in seconds.",
        },
        {
            title: "Find cross-discipline talent.",
            body: "Search “designer + biologist” or “engineer + writer” and find real people.",
        },
        {
            title: "Mentorship goes on your record.",
            body: "Every mentee's win reflects back on your ledger — no more invisible labor.",
        },
        {
            title: "No content creation, no hot takes.",
            body: "The work is the voice.",
        },
    ];
    const accents = ["#FF2052", "#FFC107", "#3EB489", "#6F00FF", "#8BCDF8"];
    return (
        <section
            className="mt-20 sm:mt-24 max-w-6xl mx-auto text-left"
            data-testid="home-bullets"
        >
            <h2
                className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter text-white text-center"
                style={{ textShadow: "0 2px 20px rgba(10,37,64,0.35)" }}
            >
                9 reasons to sign up.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((b, i) => (
                    <div
                        key={i}
                        className="crystal rounded-3xl p-5"
                        data-testid={`home-bullet-${i}`}
                    >
                        <div className="flex items-start gap-3">
                            <span
                                className="w-8 h-8 rounded-2xl flex-shrink-0 flex items-center justify-center text-sm font-display font-black text-white"
                                style={{ background: accents[i % accents.length] }}
                            >
                                {i + 1}
                            </span>
                            <div className="min-w-0">
                                <div className="font-display font-bold text-base text-slate-900 leading-tight">
                                    {b.title}
                                </div>
                                <div className="text-sm text-slate-600 leading-snug mt-1">
                                    {b.body}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Vet callout — full-width, warm, tied to the special-offer flow */}
            <div
                className="mt-8 rounded-3xl p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4"
                style={{
                    background: "linear-gradient(135deg, #FFF7ED, #FEF3C7)",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
                }}
                data-testid="home-vet-cta"
            >
                <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-amber-600 text-white flex-shrink-0">
                        <Star className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display font-black text-lg sm:text-xl text-amber-900 tracking-tight">
                            For any Veteran
                        </div>
                        <div className="text-sm text-amber-800 leading-relaxed mt-1 max-w-2xl">
                            We know it's difficult to come home and start over. Let us know you're
                            a veteran and receive our special thank-you gift —{" "}
                            <span className="font-bold">50% off Atlas, forever.</span>{" "}
                            Sign up, upload your service record, redact anything you want private,
                            and we'll take care of the rest.
                        </div>
                    </div>
                </div>
                <button
                    onClick={onVetCta}
                    className="arcade-btn"
                    data-variant="primary"
                    data-testid="home-vet-cta-btn"
                >
                    Claim your 50% off <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </section>
    );
}

